import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useGetAppealQuery } from "../store/api";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--text-secondary);
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: var(--text-primary);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Card = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
`;

const Value = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.status) {
      case "PENDING":
        return "var(--status-pending-bg)";
      case "IN_PROGRESS":
        return "var(--status-processing-bg)";
      case "APPROVED":
        return "var(--status-success-bg)";
      case "REJECTED":
        return "var(--status-error-bg)";
      default:
        return "var(--bg-secondary)";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "PENDING":
        return "var(--status-pending-text)";
      case "IN_PROGRESS":
        return "var(--status-processing-text)";
      case "APPROVED":
        return "var(--status-success-text)";
      case "REJECTED":
        return "var(--status-error-text)";
      default:
        return "var(--text-secondary)";
    }
  }};
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FileItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-primary);
    background: var(--bg-hover);
  }
`;

const FileIcon = styled.div`
  width: 2rem;
  height: 2rem;
  background: var(--bg-secondary);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
`;

const FileName = styled.span`
  font-size: 0.75rem;
  text-align: center;
  word-break: break-all;
`;

const CommentBox = styled.div`
  background: var(--bg-primary);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  white-space: pre-wrap;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--accent-error);
`;

function AppealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: appeal, isLoading, error } = useGetAppealQuery(id!);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "На рассмотрении";
      case "IN_PROGRESS":
        return "В обработке";
      case "APPROVED":
        return "Решена";
      case "REJECTED":
        return "Отклонена";
      default:
        return status;
    }
  };

  const parseFileArray = (fileData: any) => {
    if (!fileData) return [];
    if (typeof fileData === "string") {
      try {
        return JSON.parse(fileData);
      } catch {
        return [];
      }
    }
    return Array.isArray(fileData) ? fileData : [];
  };

  if (isLoading) {
    return <LoadingContainer>Загрузка апелляции...</LoadingContainer>;
  }

  if (error || !appeal) {
    return <ErrorContainer>Ошибка загрузки апелляции</ErrorContainer>;
  }

  const images = parseFileArray(appeal.images);
  const videos = parseFileArray(appeal.videos);
  const documents = parseFileArray(appeal.documents);
  const allFiles = [...images, ...videos, ...documents];

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate("/appeals")}>
          ← Назад к апелляциям
        </BackButton>
        <Title>Апелляция #{appeal.id.substring(0, 8)}</Title>
      </Header>

      <Card>
        <SectionTitle>Основная информация</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <Label>ID Апелляции</Label>
            <Value>{appeal.id}</Value>
          </InfoItem>
          <InfoItem>
            <Label>ID Ордера</Label>
            <Value>{appeal.order?.invId || appeal.orderId}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Статус</Label>
            <StatusBadge status={appeal.status}>
              {getStatusText(appeal.status)}
            </StatusBadge>
          </InfoItem>
          <InfoItem>
            <Label>Создана</Label>
            <Value>{formatDate(appeal.createdAt)}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Обновлена</Label>
            <Value>
              {appeal.updatedAt ? formatDate(appeal.updatedAt) : "—"}
            </Value>
          </InfoItem>
          <InfoItem>
            <Label>Причина</Label>
            <Value>{appeal.reason}</Value>
          </InfoItem>
        </InfoGrid>
      </Card>

      {appeal.order && (
        <Card>
          <SectionTitle>Детали ордера</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <Label>Сумма</Label>
              <Value>
                {appeal.order.amount} {appeal.order.currency}
              </Value>
            </InfoItem>
            <InfoItem>
              <Label>Статус ордера</Label>
              <Value>{appeal.order.status}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Банк</Label>
              <Value>{appeal.order.bank}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Мерчант</Label>
              <Value>{appeal.order.merchant?.displayName}</Value>
            </InfoItem>
          </InfoGrid>
        </Card>
      )}

      {appeal.comment && (
        <Card>
          <SectionTitle>Комментарий</SectionTitle>
          <CommentBox>{appeal.comment}</CommentBox>
        </Card>
      )}

      {allFiles.length > 0 && (
        <Card>
          <SectionTitle>Прикрепленные файлы ({allFiles.length})</SectionTitle>
          <FileGrid>
            {allFiles.map((file, index) => (
              <FileItem
                key={index}
                href={file.path}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileIcon>
                  {file.type === "image" && "🖼️"}
                  {file.type === "video" && "🎥"}
                  {file.type === "document" && "📄"}
                </FileIcon>
                <FileName>
                  {file.originalName || file.path?.split("/").pop() || "Файл"}
                </FileName>
              </FileItem>
            ))}
          </FileGrid>
        </Card>
      )}
    </Container>
  );
}

export default AppealDetail;
